import { Divider, Stack, Text } from "@mantine/core";
import { FieldArray, FieldArrayRenderProps, FormikProps } from "formik";
import {
  BulkReceiptItemFormValues,
  BulkReceiptItemsFormValues,
  MAX_BULK_ITEMS,
  computeRunningRemaining,
  generateClientId,
} from "pages/Debt/components/BulkReceiptCreateForm/config";
import { Fragment, useCallback, useMemo, useRef } from "react";
import BulkReceiptGroup from "./components/BulkReceiptGroup/BulkReceiptGroup";
import BulkReceiptRow, {
  BulkEditableField,
  BulkItemErrors,
  BulkItemTouched,
} from "./components/BulkReceiptGroup/components/BulkReceiptRow/BulkReceiptRow";

interface BulkReceiptAmountsStepProps {
  formik: FormikProps<BulkReceiptItemsFormValues>;
}

/**
 * Nội dung bước 1 của modal tạo nhiều phiếu thu: mỗi công nợ là một nhóm
 * (BulkReceiptGroup), mỗi nhóm chứa một hoặc nhiều phiếu thu (BulkReceiptRow)
 * chỉ gồm số tiền / ngày thu / ghi chú — chưa có phương thức thanh toán.
 */
const BulkReceiptAmountsStep = ({ formik }: BulkReceiptAmountsStepProps) => {
  const formikRef = useRef(formik);
  formikRef.current = formik;

  const arrayHelpersRef = useRef<FieldArrayRenderProps | null>(null);

  const handleFieldChange = useCallback(
    (
      index: number,
      field: BulkEditableField,
      value: number | "" | Date | null | string,
    ) => {
      formikRef.current.setFieldValue(`items[${index}].${field}`, value, false);
    },
    [],
  );

  const handleFieldBlur = useCallback(
    (index: number, field: "amount" | "paymentDate") => {
      formikRef.current.setFieldTouched(`items[${index}].${field}`, true);
    },
    [],
  );

  const handleDuplicate = useCallback((index: number) => {
    const items = formikRef.current.values.items;
    if (items.length >= MAX_BULK_ITEMS) return;
    const source = items[index];
    // Dòng mới tự fill đúng số tiền còn thiếu tính đến thời điểm này, thay vì
    // để trống hoặc lặp lại nguyên số dư gốc của công nợ.
    const remainingAfterSource = computeRunningRemaining(items)[index];
    const duplicate: BulkReceiptItemFormValues = {
      ...source,
      clientId: generateClientId(),
      amount: remainingAfterSource > 0 ? remainingAfterSource : 0,
      notes: "",
      paymentDate: new Date(),
    };
    arrayHelpersRef.current?.insert(index + 1, duplicate);
  }, []);

  const handleRemove = useCallback((index: number) => {
    if (formikRef.current.values.items.length <= 1) return;
    arrayHelpersRef.current?.remove(index);
  }, []);

  const items = formik.values.items;
  const runningRemaining = useMemo(() => computeRunningRemaining(items), [items]);

  // Gom các dòng liền kề cùng saleOrderId thành một nhóm công nợ. Do dòng mới
  // luôn được chèn ngay sau dòng nguồn (cùng công nợ), các dòng cùng nhóm
  // luôn nằm liền nhau trong mảng items.
  const groups = useMemo(() => {
    const result: { saleOrderId: string; startIndex: number; endIndex: number }[] = [];
    items.forEach((item, index) => {
      const last = result[result.length - 1];
      if (last && last.saleOrderId === item.saleOrderId) {
        last.endIndex = index;
      } else {
        result.push({ saleOrderId: item.saleOrderId, startIndex: index, endIndex: index });
      }
    });
    return result;
  }, [items]);

  const canAddMore = items.length < MAX_BULK_ITEMS;
  const canRemove = items.length > 1;

  const itemsErrors = Array.isArray(formik.errors.items)
    ? (formik.errors.items as Array<BulkItemErrors | undefined>)
    : undefined;
  const itemsTouched = Array.isArray(formik.touched.items)
    ? (formik.touched.items as Array<BulkItemTouched | undefined>)
    : undefined;
  const itemsError =
    typeof formik.errors.items === "string" ? formik.errors.items : null;

  return (
    <Stack gap="md">
      {itemsError && (
        <Text size="sm" c="red">
          {itemsError}
        </Text>
      )}

      <FieldArray name="items">
        {(helpers) => {
          arrayHelpersRef.current = helpers;
          return (
            <Stack gap="md">
              {groups.map((group) => {
                const first = items[group.startIndex];
                return (
                  <BulkReceiptGroup
                    key={group.saleOrderId}
                    debtCode={first.debtCode}
                    partnerName={first.partnerName}
                    saleOrderCode={first.saleOrderCode}
                    remaining={runningRemaining[group.endIndex]}
                    groupEndIndex={group.endIndex}
                    onAddReceipt={handleDuplicate}
                    canAddMore={canAddMore}
                  >
                    {Array.from(
                      { length: group.endIndex - group.startIndex + 1 },
                      (_, i) => {
                        const index = group.startIndex + i;
                        const item = items[index];
                        return (
                          <Fragment key={item.clientId}>
                            {i > 0 && <Divider variant="dashed" />}
                            <BulkReceiptRow
                              index={index}
                              receiptNumber={i + 1}
                              item={item}
                              itemError={itemsErrors?.[index]}
                              itemTouched={itemsTouched?.[index]}
                              onFieldChange={handleFieldChange}
                              onFieldBlur={handleFieldBlur}
                              onRemove={handleRemove}
                              canRemove={canRemove}
                            />
                          </Fragment>
                        );
                      },
                    )}
                  </BulkReceiptGroup>
                );
              })}
            </Stack>
          );
        }}
      </FieldArray>
    </Stack>
  );
};

export default BulkReceiptAmountsStep;
